// oxlint-disable complexity
// oxlint-disable max-statements
// oxlint-disable max-lines-per-function
import { isPlatformBrowser } from '@angular/common';
import type { Signal } from '@angular/core';
import { inject, PendingTasks, PLATFORM_ID, RESPONSE_INIT } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
    RedirectCommand,
    Router,
    type ActivatedRouteSnapshot,
    type CanActivateFn,
    type UrlTree,
} from '@angular/router';
import { isDefined } from '@hyundai/shared-lib';
import type { Action, MemoizedSelector } from '@ngrx/store';
import { Store } from '@ngrx/store';
import { filter, isObservable, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { injectRequestContext, setRedirect, type ReqContext } from '../utilities/redirect';

export type Loadable<T> = T | null | false;
export type Loaded<T> = Exclude<T, { error: string } | null | false>;

export function isLoaded<T extends object>(value: Loadable<T>): value is T {
    return Boolean(value) && typeof value === 'object';
}

export function toLoaded<T>(value: Signal<Loadable<T>>): Signal<T | false | undefined>;
export function toLoaded<T>(value: Observable<Loadable<T>>): Observable<T | false>;
export function toLoaded<T>(
    value: Observable<Loadable<T>> | Signal<Loadable<T>>,
): Signal<T | false | undefined> | Observable<T | false> {
    const taskCleanup = inject(PendingTasks).add();
    const obs$ = isObservable(value) ? value : toObservable(value);
    const toLoaded = obs$.pipe(
        filter(isDefined),
        tap(() => {
            taskCleanup();
        }),
    );
    if (isObservable(value)) {
        return toLoaded;
    }
    return toSignal(toLoaded);
}

export interface NgrxLoaderGuardOptions<TData, TState> {
    actionToDispatch?: Action | ((route: ActivatedRouteSnapshot) => Action);
    waitUntilLoadedSelector?: MemoizedSelector<TState, boolean>;
    dataToCheckSelector?: MemoizedSelector<TState, TData>;
    verifyData?: (data: TData) => boolean;
    redirectOnFailUrl?: string | ((route: ActivatedRouteSnapshot, store: Store) => string | Observable<string>);
    actionToDispatchOnSuccess?: Action | ((route: ActivatedRouteSnapshot) => Action);
    redirectOnFailCode?: number;
}

interface LoaderContext {
    route: ActivatedRouteSnapshot;
    router: Router;
    store: Store;
    isBrowser: boolean;
    responseInit?: ResponseInit | null;
    reqContext: ReqContext;
}

export function ngrxLoaderGuard<TData = unknown, TState = unknown>(
    opt: NgrxLoaderGuardOptions<TData, TState>,
): CanActivateFn {
    return function (route) {
        const store = inject(Store);
        const router = inject(Router);
        const responseInit = inject(RESPONSE_INIT, { optional: true });
        const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        const reqContext = injectRequestContext();

        const ctx: LoaderContext = {
            route,
            router,
            store,
            isBrowser,
            responseInit,
            reqContext,
        };
        if (opt.actionToDispatch) {
            if (typeof opt.actionToDispatch === 'function') {
                store.dispatch(opt.actionToDispatch(route));
            } else {
                store.dispatch(opt.actionToDispatch);
            }
        }

        if (!opt.waitUntilLoadedSelector) {
            return true;
        }

        const waitForLoad = store.select(opt.waitUntilLoadedSelector).pipe(
            filter((isLoading) => !isLoading),
            take(1),
        );

        const dataSelector = opt.dataToCheckSelector;

        if (!dataSelector) {
            return waitForLoad;
        }
        return waitForLoad.pipe(
            switchMap(() => store.select(dataSelector)),
            switchMap((data) => {
                if ((opt.verifyData && opt.verifyData(data)) || (!opt.verifyData && data)) {
                    if (typeof opt.actionToDispatchOnSuccess === 'function') {
                        store.dispatch(opt.actionToDispatchOnSuccess(route));
                    } else if (opt.actionToDispatchOnSuccess) {
                        store.dispatch(opt.actionToDispatchOnSuccess);
                    }
                    return of(true);
                }

                return handleRedirectOnFailUrl(opt, ctx);
            }),
        );
    } satisfies CanActivateFn;
}

function handleRedirectOnFailUrl<TData = unknown, TState = unknown>(
    opt: NgrxLoaderGuardOptions<TData, TState>,
    ctx: LoaderContext,
): Observable<UrlTree | boolean | RedirectCommand> {
    const { route, router, store, isBrowser, responseInit } = ctx;
    const redirectCode = opt.redirectOnFailCode ?? 302;

    if (opt.redirectOnFailUrl) {
        if (typeof opt.redirectOnFailUrl === 'function') {
            const res = opt.redirectOnFailUrl(route, store);
            if (res instanceof Observable) {
                return res.pipe(
                    take(1),
                    map((url) => {
                        if (responseInit) {
                            setRedirect(ctx.reqContext, redirectCode, url);
                            return false;
                        }
                        return router.createUrlTree([url]);
                    }),
                );
            }
            if (responseInit) {
                setRedirect(ctx.reqContext, redirectCode, res);
                return of(false);
            }
            return of(router.createUrlTree([res]));
        }

        if (responseInit) {
            setRedirect(ctx.reqContext, redirectCode, opt.redirectOnFailUrl);
            return of(false);
        }
        if (opt.redirectOnFailUrl.startsWith('http')) {
            if (isBrowser) {
                window.location.href = opt.redirectOnFailUrl;
            }

            return of(false);
        }
        return of(router.createUrlTree([opt.redirectOnFailUrl]));
    }
    return of(false);
}
